import * as React from "react";
import { DEFAULT_ZIGGY_VARIANT, isZiggyVariant, type ZiggyVariant } from "./ziggy";

/**
 * Ziggy look context + hook, split out from `ziggyProvider.tsx` so that file
 * exports only the `<ZiggyVariantProvider>` component (react-refresh
 * convention — mirrors `sessionContext.ts` / `session.tsx`).
 *
 * Each learner chooses a pink or blue Ziggy on first run (`chosen` gates the
 * one-time chooser) and can switch anytime from Profile. Persisted to
 * localStorage; the institution palette still tints Ziggy's accents on top
 * (hybrid identity, phase-P §4).
 */

export const ZIGGY_VARIANT_KEY = "eos:ziggy-variant";
export const ZIGGY_CHOSEN_KEY = "eos:ziggy-chosen";

export interface ZiggyVariantContextValue {
  variant: ZiggyVariant;
  /** Whether the learner has explicitly picked a look (else show chooser). */
  chosen: boolean;
  setVariant: (variant: ZiggyVariant) => void;
}

export const ZiggyVariantContext = React.createContext<ZiggyVariantContextValue | null>(null);

export function readStoredZiggy(): { variant: ZiggyVariant; chosen: boolean } {
  try {
    const stored = window.localStorage.getItem(ZIGGY_VARIANT_KEY);
    const chosen = window.localStorage.getItem(ZIGGY_CHOSEN_KEY) === "1";
    if (isZiggyVariant(stored)) return { variant: stored, chosen };
  } catch {
    /* storage unavailable (private mode / SSR) — fall through to default */
  }
  return { variant: DEFAULT_ZIGGY_VARIANT, chosen: false };
}

export function useZiggyVariant(): ZiggyVariantContextValue {
  const ctx = React.useContext(ZiggyVariantContext);
  if (!ctx) throw new Error("useZiggyVariant must be used within a ZiggyVariantProvider");
  return ctx;
}
